import { Box } from '@chakra-ui/react';
import { ReactComponent as PlusCircleIcon } from 'assets/icons/plusCircle.svg';
import { ReactComponent as PlusCircleOutlineIcon } from 'assets/icons/plusCircleOutline.svg';
import { LINE_CREATED, PORT_NODE_CONNECTED } from 'constants/mixpanel';
import { usePrevious } from 'hooks/usePrevious';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getLineOrientation, getLineType } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { addNewWidget, useAppDispatch } from 'modules/Editor/store';
import { DragHandler } from 'modules/common/components/DragHandler';
import { MouseEvent, ReactElement, RefObject, createRef, memo, useCallback, useMemo, useRef, useState } from 'react';
import { OnDrag, OnDragEnd, OnDragStart } from 'react-moveable';
import { PortRendererProps, PortTypes } from 'types/flowCore.types';
import { GroupTargetData } from 'types/flowCore.types';
import { WidgetType } from 'types/widget.types';
import {
  detectNearestGroupElement,
  detectPortElement,
  detectWidgetElementWithProximity,
  getDOMMatrix,
  getParentWidgetElement,
} from 'utils/dom';
import { GroupWidgetData } from 'widgets/GroupWidget/GroupWidget.types';
import { LINE_TYPE_MAP } from 'widgets/LineWidget/LineRenders';
import { DEFAULT_DELTA_POS } from 'widgets/LineWidget/LineWidget.config';
import {
  calculateTargetPoint,
  calculateWidgetRect,
  generateDefaultData,
  getPositionMiddleOfSide,
} from 'widgets/LineWidget/LineWidget.helpers';
import {
  Alignment,
  DeltaPos,
  LineWidgetStyle,
  LineWidgetTypes,
  PortData,
  Rect,
  Side,
} from 'widgets/LineWidget/LineWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import {
  generateWrapperStyle,
  getFlowModeWidgetSelector,
  getOppositeSide,
  getWrapperOffset,
} from '../FlowCore.helpers';
import { CREATOR_HANDLE_SELECTOR_SIZE, HANDLE_ICON_SIZE, HANDLE_SIDE_LIST, LINE_CREATOR_PADDING } from '../Port.config';
import { useNodeModalRender, usePortRender, usePortValue } from '../Port.hooks';
import { computePortLineStyle } from './LineCreator.helpers';
import PortSelector from './PortSelector';
import { useGetWidgetDataRect } from './PortStepsGrid.hooks';
import PortWrapper from './PortWrapper';

// NOTE: It's only for the straight line.
// This default posList constant value will be refactored when I implement a stepped or curved line
const DEFAULT_POS_LIST = [
  { xPx: 0, yPx: 0 },
  { xPx: 0, yPx: 0 },
];

const DEFAULT_ADD_NODE_BUFFER_PX = 30;

function LineCreator({ zoom }: PortRendererProps): ReactElement {
  const [renderNodeModal, closeNodeModal] = useNodeModalRender();
  const dispatch = useAppDispatch();

  const [delta, setDelta] = useState<DeltaPos>(DEFAULT_DELTA_POS);
  const [isHover, setIsHover] = useState(false);

  // Line style for the line that will be created
  const [lineStyle, setLineStyle] = useState<LineWidgetStyle>({
    strokeWidth: 3 * zoom,
    strokeColor: 'black',
    strokeDashType: 'solid',
    startArrowStyle: 'none',
    endArrowStyle: 'none',
    type: LineWidgetTypes.straight,
  });

  const [startPortPos, setStartPortPos] = useState({ topPx: 0, leftPx: 0 });

  // When dragging or resizing, these hold the real-time rect values of the start and end widgets
  const [realTimeEndRect, setRealTimeEndRect] = useState<Rect | null>(null);
  // When dragging or resizing, these hold the real-time start/end ports of the line
  const [realTimeEndPort, setRealTimeEndPort] = useState<PortData | null>(null);

  const targetHandlerElmRefs = useRef<RefObject<HTMLDivElement>[]>(HANDLE_SIDE_LIST.map(() => createRef()));
  const currentSide = useRef<Side | null>(null);
  const isDragging = useRef(false);
  const showNodeCreatorModal = useRef(false);

  // PortContext
  const portData = usePortValue('creatorPortData');
  const lastPortData = usePrevious(portData);
  const selectedPort = usePortValue('selectedPort');
  const [renderPort, clearPort] = usePortRender();
  const [targetDragGroupElement, setTargetDragGroupElement] = useState<GroupTargetData | null>(null);

  const { getWidgetBoundingRectData } = useGetWidgetDataRect();
  const { getWidgetDataOfAllGroups, getRectOfGroupFromWidgetIdAsRect } = useGetWidgetDataRect();
  const [allGroupWidgets, setAllGroupWidgets] = useState<{ widgetId: string; widgetData: GroupWidgetData }[]>([]);

  // Reset the currentSide only when active portData changes from not null to null
  if (currentSide.current && portData === null && lastPortData !== null) {
    showNodeCreatorModal.current = false;
    currentSide.current = null;
  }

  // Generate the port wrapper positions using the portData.position
  const sideHandlerList = useMemo(
    () =>
      portData
        ? HANDLE_SIDE_LIST.map((side) => ({
            side,
            style: generateWrapperStyle(portData.position, side, zoom, 0, portData.rotation),
            styleWithPadding: generateWrapperStyle(
              portData.position,
              side,
              zoom,
              LINE_CREATOR_PADDING / zoom,
              portData.rotation,
            ),
            offset: getWrapperOffset(portData.position, side, zoom, LINE_CREATOR_PADDING / zoom, portData.rotation),
          }))
        : [],
    [portData, zoom],
  );

  const handleDragStart = useCallback(
    (e: OnDragStart) => {
      isDragging.current = true;

      const portSide = e.target.dataset?.portSide as Side;
      if (!portSide) return e.stopDrag();

      e.target.style.pointerEvents = 'none';

      currentSide.current = portSide;
      setDelta(DEFAULT_DELTA_POS);

      setRealTimeEndRect(null);
      setRealTimeEndPort(null);

      closeNodeModal();

      setAllGroupWidgets(getWidgetDataOfAllGroups());
    },
    [closeNodeModal, getWidgetDataOfAllGroups],
  );

  const handleDrag = useCallback(
    (e: OnDrag, translate: OnDrag['translate']) => {
      const selectedHandler = sideHandlerList.find((data) => data.side === currentSide.current);
      if (!portData || !selectedHandler) return e.stopDrag();

      // Detect widget elements that require port rendering
      const targetWidgetBaseElement = detectWidgetElementWithProximity(e.clientX, e.clientY);
      const portElement = detectPortElement(e.clientX, e.clientY);

      const targetGroupData = detectNearestGroupElement(e.clientX, e.clientY, allGroupWidgets, true);
      const finalGroupData = !!targetGroupData
        ? targetGroupData
        : !targetWidgetBaseElement && !!targetDragGroupElement
        ? targetDragGroupElement
        : null;

      // Ignore port rendering when trying to connect itself
      const ignoreConnectorWidget = portData.widgetId === targetWidgetBaseElement?.id;
      if (!portElement) {
        let targetElement = targetWidgetBaseElement && !ignoreConnectorWidget ? targetWidgetBaseElement : null;

        setRealTimeEndRect(null);
        setRealTimeEndPort({
          widgetId: '',
          alignment: Alignment.CENTER,
          side: getOppositeSide(currentSide.current!),
        });

        if (finalGroupData) {
          setTargetDragGroupElement(finalGroupData);
          renderPort(null, PortTypes.Connector, finalGroupData);
        } else if (targetElement) {
          // Responsive widget use the root widget as target.
          const responsiveWidgetElement = getParentWidgetElement(targetElement);
          targetElement = responsiveWidgetElement || targetElement;
          setTargetDragGroupElement(null);
          renderPort(targetElement, PortTypes.Connector);
        }
      } else {
        // Get the responsive widget element if it exists
        const responsiveWidgetElement = !!targetWidgetBaseElement
          ? getParentWidgetElement(targetWidgetBaseElement)
          : null;

        // Set target element id to be the responsive widget id or non responsive widget id
        let targetElementId = !!responsiveWidgetElement
          ? responsiveWidgetElement.id
          : !!targetWidgetBaseElement
          ? targetWidgetBaseElement?.id
          : null;
        let widgetRectData = null;
        if (finalGroupData) {
          widgetRectData = getRectOfGroupFromWidgetIdAsRect(finalGroupData?.groupId);
        } else if (targetElementId) {
          widgetRectData = getWidgetBoundingRectData(targetElementId)?.widgetRectData;
        }

        // Set the real time end port to the selected port if exists, otherwise
        // Create a pseudo port for the node creator modal
        if (!!selectedPort) {
          setRealTimeEndPort(selectedPort);
          setRealTimeEndRect(widgetRectData);
        } else {
          setRealTimeEndPort({
            widgetId: '',
            alignment: Alignment.CENTER,
            side: getOppositeSide(currentSide.current!),
          });
          setRealTimeEndRect(null);
        }
      }

      // Update line path
      setDelta({
        targetIndex: DEFAULT_POS_LIST.length - 1,
        xPx: translate[0] + selectedHandler.offset.xPx,
        yPx: translate[1] + selectedHandler.offset.yPx,
      });
    },
    [
      sideHandlerList,
      portData,
      allGroupWidgets,
      targetDragGroupElement,
      renderPort,
      selectedPort,
      getRectOfGroupFromWidgetIdAsRect,
      getWidgetBoundingRectData,
    ],
  );

  const handleDragEnd = useCallback(
    (e: OnDragEnd) => {
      const selectedHandler = sideHandlerList.find((data) => data.side === currentSide.current);
      if (!(selectedHandler && delta && currentSide.current && DEFAULT_POS_LIST && portData)) return;

      const originStyle = generateWrapperStyle(portData.position, currentSide.current, 1, 0, portData.rotation);
      const originWrapperPos = {
        xPx: parseInt(originStyle.left as string),
        yPx: parseInt(originStyle.top as string),
      };

      // If the port exists, update the delta value so that the arrow moves to the center of the port
      let scaledDelta = {
        targetIndex: delta.targetIndex,
        xPx: delta.xPx / zoom,
        yPx: delta.yPx / zoom,
      };

      if (selectedPort) {
        if (getWidgetTypeFromId(selectedPort.widgetId) === WidgetType.Group) {
          const groupTargetData = targetDragGroupElement;
          if (!!groupTargetData) {
            const lastPosIndex = 1;
            // Calculate the port position based on the side.
            const [targetX, targetY] = getPositionMiddleOfSide({
              leftPx: groupTargetData.wrapperPos.leftPx,
              topPx: groupTargetData.wrapperPos.topPx,
              widthPx: groupTargetData.wrapperPos.widthPx,
              heightPx: groupTargetData.wrapperPos.heightPx,
              side: selectedPort.side,
            });
            const offsetX = DEFAULT_POS_LIST[lastPosIndex].xPx + scaledDelta.xPx + originWrapperPos.xPx - targetX;
            const offsetY = DEFAULT_POS_LIST[lastPosIndex].yPx + scaledDelta.yPx + originWrapperPos.yPx - targetY;
            scaledDelta.xPx -= offsetX;
            scaledDelta.yPx -= offsetY;
          }
        } else {
          const targetWidgetElement = document.querySelector(
            getFlowModeWidgetSelector(selectedPort.widgetId),
          ) as HTMLDivElement;
          if (targetWidgetElement) {
            const matrix = getDOMMatrix(targetWidgetElement);
            const portPos = calculateTargetPoint(targetWidgetElement, matrix, selectedPort);
            const lastPosIndex = portPos.length - 1;
            const offsetX = DEFAULT_POS_LIST[lastPosIndex].xPx + scaledDelta.xPx + originWrapperPos.xPx - portPos[0];
            const offsetY = DEFAULT_POS_LIST[lastPosIndex].yPx + scaledDelta.yPx + originWrapperPos.yPx - portPos[1];

            scaledDelta.xPx -= offsetX;
            scaledDelta.yPx -= offsetY;
          }
        }
      } else {
        // f there is no port to connect to, if the delta is 0 (means the user just clicked on the port add button)
        // Add a buffer to the delta so that there is a gap between the 2 widgets
        if (scaledDelta.xPx === 0 && scaledDelta.yPx === 0) {
          scaledDelta.targetIndex = 1;
          switch (selectedHandler.side) {
            case Side.NORTH:
              scaledDelta.yPx -= DEFAULT_ADD_NODE_BUFFER_PX;
              break;
            case Side.SOUTH:
              scaledDelta.yPx += DEFAULT_ADD_NODE_BUFFER_PX;
              break;
            case Side.WEST:
              scaledDelta.xPx -= DEFAULT_ADD_NODE_BUFFER_PX;
              break;
            case Side.EAST:
              scaledDelta.xPx += DEFAULT_ADD_NODE_BUFFER_PX;
              break;
          }
        }
      }

      const lineWidgetData = calculateWidgetRect(
        originWrapperPos.xPx,
        originWrapperPos.yPx,
        scaledDelta,
        DEFAULT_POS_LIST,
      );
      const widgetData = generateDefaultData(LineWidgetTypes.stepped, {
        ...lineWidgetData,
        ...lineStyle,
        startPort: {
          widgetId: portData.widgetId,
          alignment: Alignment.CENTER,
          side: currentSide.current,
        },
        endPort: selectedPort,
      });

      if (widgetData && selectedPort) {
        // If there is a port that can be connected, add a new line
        dispatch(addNewWidget(widgetData));

        const { startPos, endPos, startArrowStyle, endArrowStyle, type } = widgetData.widgetData;
        Mixpanel.track(LINE_CREATED.eventName, {
          orientation: getLineOrientation(startPos, endPos),
          line_start: startArrowStyle === 'none' ? LINE_CREATED.lineEdge.None : LINE_CREATED.lineEdge.BasicArrow,
          line_end: endArrowStyle === 'none' ? LINE_CREATED.lineEdge.None : LINE_CREATED.lineEdge.BasicArrow,
          type: getLineType(type),
          from: LINE_CREATED.from.FlowModePort,
        });
        Mixpanel.track(PORT_NODE_CONNECTED.eventName);

        // Reset
        currentSide.current = null;
        setDelta(DEFAULT_DELTA_POS);
        clearPort();

        setRealTimeEndPort(null);
        setRealTimeEndRect(null);
      } else {
        // Render the new widget creator modal if there is no port to connect
        if (!selectedPort) {
          const endX = lineWidgetData.leftPx + lineWidgetData.endPos.xPx;
          const endY = lineWidgetData.topPx + lineWidgetData.endPos.yPx;
          renderNodeModal({
            widgetId: portData.widgetId,
            position: {
              leftPx: endX,
              topPx: endY,
              widthPx: 0,
              heightPx: 0,
            },
            lineWidgetData: widgetData,
          });

          showNodeCreatorModal.current = true;
        }
      }

      // defer the reset state to the next event loop to avoid render right away
      setTimeout(() => {
        setTargetDragGroupElement(null);
        setAllGroupWidgets([]);
      }, 0);

      isDragging.current = false;
    },
    [
      sideHandlerList,
      delta,
      portData,
      zoom,
      selectedPort,
      lineStyle,
      targetDragGroupElement,
      dispatch,
      clearPort,
      renderNodeModal,
    ],
  );

  // Manage hover state
  const handleMouseOut = () => setIsHover(false);
  const handleMouseOver = (e: MouseEvent<HTMLDivElement>) => {
    const portSide = e.currentTarget.dataset?.portSide as Side;
    if (!portSide || showNodeCreatorModal.current || isDragging.current) return;

    // Update currentSide
    if (currentSide.current !== portSide) currentSide.current = portSide;

    if (portData) {
      // Update the line style
      const lineStyle = computePortLineStyle(portData.widgetId, portSide);
      if (lineStyle) setLineStyle(lineStyle);

      // Calculate the port position based on the side.
      const isGroupWidget = getWidgetTypeFromId(portData.widgetId) === WidgetType.Group;
      const widgetRect = isGroupWidget
        ? getRectOfGroupFromWidgetIdAsRect(portData?.widgetId)
        : getWidgetBoundingRectData(portData.widgetId)?.widgetRectData;
      if (widgetRect) {
        const { top, left } = generateWrapperStyle(
          {
            leftPx: widgetRect.x,
            topPx: widgetRect.y,
            widthPx: widgetRect.width,
            heightPx: widgetRect.height,
          },
          portSide,
        );

        setStartPortPos({
          topPx: parseFloat(top as string),
          leftPx: parseFloat(left as string),
        });
      }
    }

    // Initilize the last delta state
    setDelta(DEFAULT_DELTA_POS);
    setIsHover(true);
  };

  const Line = useMemo(() => LINE_TYPE_MAP[lineStyle.type] as React.ElementType, [lineStyle.type]);

  return (
    <Box>
      {sideHandlerList.map(({ side, style, styleWithPadding }, index) => (
        <div key={side}>
          {
            <div>
              <PortWrapper
                ref={targetHandlerElmRefs.current[index]}
                style={styleWithPadding}
                data-port-side={side}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}
              >
                {!isDragging.current && !showNodeCreatorModal.current && (
                  <Box
                    position='absolute'
                    color='upgrade.blue.500'
                    left={`${(-HANDLE_ICON_SIZE - 1) / 2}px`}
                    top={`${(-HANDLE_ICON_SIZE - 1) / 2}px`}
                    width={`${HANDLE_ICON_SIZE}px`}
                    height={`${HANDLE_ICON_SIZE}px`}
                  >
                    {isHover && currentSide.current === side ? (
                      <PlusCircleIcon width='100%' height='100%' />
                    ) : (
                      <PlusCircleOutlineIcon width='100%' height='100%' />
                    )}
                  </Box>
                )}
                <PortSelector data-port-side={side} size={CREATOR_HANDLE_SELECTOR_SIZE} />
              </PortWrapper>

              {targetHandlerElmRefs.current[index] ? (
                <DragHandler
                  targetRef={targetHandlerElmRefs.current[index]}
                  onDragStart={handleDragStart}
                  onDrag={handleDrag}
                  onDragEnd={handleDragEnd}
                  enableSmartGuide={false}
                />
              ) : null}
            </div>
          }
          <PortWrapper key={side} style={{ ...style }}>
            {side === currentSide.current && (isDragging.current || showNodeCreatorModal.current) && (
              <Line
                posList={DEFAULT_POS_LIST}
                delta={isDragging.current || showNodeCreatorModal.current ? delta : DEFAULT_DELTA_POS}
                strokeWidth={lineStyle.strokeWidth * zoom}
                strokeColor={lineStyle.strokeColor}
                strokeDashType={lineStyle.strokeDashType}
                startArrowStyle={lineStyle.startArrowStyle}
                endArrowStyle={lineStyle.endArrowStyle}
                realTimeEndPort={realTimeEndPort}
                realTimeEndRect={realTimeEndRect}
                isUseRealTimeEndRect={!selectedPort}
                startPort={
                  portData
                    ? {
                        widgetId: portData.widgetId,
                        alignment: Alignment.CENTER,
                        side,
                      }
                    : null
                }
                endPort={selectedPort}
                topPx={startPortPos.topPx}
                leftPx={startPortPos.leftPx}
              ></Line>
            )}
          </PortWrapper>
        </div>
      ))}
    </Box>
  );
}

export default memo(LineCreator);
